import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Award, Dna, Flower2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { IMAGES, CREDENTIALS } from "@/data/content";
import { scrollToSection } from "@/App";

const CRED_ICONS = [GraduationCap, Award, Dna, Flower2];

export default function Founder() {
  return (
    <section id="about" className="py-24 sm:py-32 bg-ivory relative overflow-hidden" data-testid="founder-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="relative max-w-md mx-auto">
              <motion.div
                whileHover={{ scale: 1.015 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden rounded-t-[14rem] rounded-b-[2rem] border border-gold/30 shadow-hover"
              >
                <img
                  src={IMAGES.founder}
                  alt="Dr. Anjali — Founder of Aarogyam Bharat, in calm natural light"
                  className="w-full aspect-[3/4] object-cover"
                  loading="lazy"
                />
              </motion.div>
              <div className="absolute -bottom-6 -right-3 sm:-right-8 bg-forest text-ivory rounded-2xl px-6 py-5 shadow-hover max-w-[240px]">
                <p className="font-serif italic text-lg leading-snug">
                  "On a Mission to Transform 1,000 Lives Till 2027"
                </p>
              </div>
            </div>
          </Reveal>

          <div className="order-1 lg:order-2">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-gold mb-4">Meet Your Coach</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-forest">Meet Dr. Anjali</h2>
              <p className="mt-3 text-sm font-semibold tracking-wide text-sage">
                PhD | Certified Health Coach | Certified Nutrigenomics Consultant | Meditation Trainer
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-7 space-y-4 text-base leading-relaxed text-inkmuted">
                <p>
                  "I believe health should not be a constant cycle of restriction, guilt and starting over. I
                  believe our body is intelligent and constantly working toward balance.
                </p>
                <p>
                  What we often need is the right environment, nourishment, movement, recovery, habits and
                  support. That's why my approach goes beyond simply telling you what to eat.
                </p>
                <p>
                  I help you understand your lifestyle, identify the patterns that may be getting in the way of
                  your health goals and build a personalised system that you can realistically follow."
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-7 font-serif italic text-xl text-botanical border-l-2 border-gold pl-5">
                "My mission is to transform 1,000 lives by 2027 through practical, personalised and sustainable
                health coaching."
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <button
                onClick={() => scrollToSection("approach")}
                data-testid="founder-discover-approach-button"
                className="mt-8 inline-flex items-center gap-2 text-sm font-bold tracking-wide text-forest hover:text-sage transition-colors group"
              >
                DISCOVER MY APPROACH
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="credentials-grid">
          {CREDENTIALS.map((c, i) => {
            const Icon = CRED_ICONS[i];
            return (
              <Reveal key={c.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.35 }}
                  className="h-full bg-cardbg border border-botanical/10 rounded-2xl p-6 shadow-card hover:shadow-hover transition-shadow duration-300"
                  data-testid={`credential-card-${i}`}
                >
                  <div className="w-11 h-11 rounded-full bg-forest flex items-center justify-center mb-4">
                    <Icon size={20} className="text-gold" />
                  </div>
                  <h3 className="font-serif text-xl text-forest leading-snug">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-inkmuted">{c.sub}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
